import { useState, useEffect, useCallback } from "react";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import {
  Upload,
  X,
  FileText,
  ExternalLink,
  Check,
  AlertCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Input } from "@shared/ui/input";
import { useDocumentSubtype } from "@features/cx/consumerManagement/hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { toast } from "@shared/ui/use-toast";

interface DocumentCardComponentProps {
  doc: any;
  index: number;
  documents: any[];
  selectedDocumentTypes: { [key: string]: string };
  selectedSubTypes: { [key: string]: string };
  documentTypeData: any;
  isEditMode: boolean;
  isDocumentTypeLoading: boolean;
  dragging: boolean;
  onDocumentTypeChange: (cardId: string, type: string) => void;
  onSubtypeChange: (cardId: string, subtype: string) => void;
  onFileUpload: (file: File, docType: string, subType: string, cardId: string) => void;
  onRemoveDocument: (docId: string) => void;
  onRemoveCard: (cardId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  isUploadDisabled: (cardId: string) => boolean;
  canRemoveCard: boolean;
  hideDocumentTypeDropdown?: boolean;
  referenceNumber: string;
  onReferenceNumberChange: (cardId: string, ref: string) => void;
}

export function DocumentCardComponent({
  doc,
  index,
  documents,
  selectedDocumentTypes,
  selectedSubTypes,
  documentTypeData,
  isEditMode,
  isDocumentTypeLoading,
  dragging,
  onDocumentTypeChange,
  onSubtypeChange,
  onFileUpload,
  onRemoveDocument,
  onRemoveCard,
  onDragOver,
  onDragLeave,
  isUploadDisabled,
  canRemoveCard,
  hideDocumentTypeDropdown = false,
  referenceNumber,
  onReferenceNumberChange,
}: DocumentCardComponentProps) {
  const remoteUtilityId = getRemoteUtilityId();
  const [loadedSubtypes, setLoadedSubtypes] = useState<any[]>([]);
  const [subtypeApiError, setSubtypeApiError] = useState<string | null>(null);
  
  // Find the actual document (not placeholder)
  const uploadedDocument = documents.find((d) => 
    d.type === doc.id && (d.file || d.url)
  );
  
  const hasUploadedDocument = !!uploadedDocument;
  
  console.log(`📋 Card ${doc.name}:`, {
    docId: doc.id,
    documents: documents,
    uploadedDocument: uploadedDocument,
    hasUploadedDocument: hasUploadedDocument,
    selectedSubtype: selectedSubTypes[doc.id]
  });
  
  // Currently selected type and subtype
  const selectedType = selectedDocumentTypes[doc.id];
  const selectedSubtype = selectedSubTypes[doc.id];
  
  // Get document code
  const getDocumentTypeCode = useCallback(() => {
    if (doc.code) {
      console.log(`Using configured doc code: "${doc.code}" for card ${doc.id}`);
      return doc.code;
    }
    
    if (selectedType && documentTypeData?.result) {
      const foundType = documentTypeData.result.find(
        (type: any) => type.name.toLowerCase() === selectedType.toLowerCase()
      );
      const typeCode = foundType?.code || "";
      console.log(`Found type code "${typeCode}" for selected type "${selectedType}"`);
      return typeCode;
    }
    
    return "";
  }, [doc.code, doc.id, selectedType, documentTypeData]);

  const documentTypeCode = getDocumentTypeCode();
  
  // Fetch document subtypes
  const {
    data: documentSubtypeData,
    isLoading: isDocumentSubtypeLoading,
    error: documentSubtypeError,
  } = useDocumentSubtype({
    remote_utility_id: remoteUtilityId,
    config_level: "document_subtype",
    code_list: documentTypeCode,
  }, {
    enabled: !!documentTypeCode,
  });

  // Handle API errors
  useEffect(() => {
    if (documentSubtypeError) {
      console.error(`Error fetching subtypes for code ${documentTypeCode}:`, documentSubtypeError);
      setSubtypeApiError(`Failed to load subtypes: ${documentSubtypeError.message}`);
    } else {
      setSubtypeApiError(null);
    }
  }, [documentSubtypeError, documentTypeCode]);

  // Store subtypes when received AND validate selected subtype
  useEffect(() => {
    if (documentSubtypeData?.result) {
      console.log(`Subtypes loaded for "${documentTypeCode}":`, documentSubtypeData.result);
      setLoadedSubtypes(documentSubtypeData.result);
      
      // FIX: Verify that the selected subtype exists in the loaded subtypes
      if (selectedSubtype) {
        const subtypeExists = documentSubtypeData.result.some(
          (st: any) => st.name === selectedSubtype
        );
        if (!subtypeExists) {
          console.warn(`⚠️ Selected subtype "${selectedSubtype}" not found in loaded subtypes for card ${doc.id}`);
          console.log('Available subtypes:', documentSubtypeData.result.map((st: any) => st.name));
        } else {
          console.log(`✅ Selected subtype "${selectedSubtype}" is valid for card ${doc.id}`);
        }
      }
    }
  }, [documentSubtypeData, documentTypeCode, selectedSubtype, doc.id]);

  const subtypes = loadedSubtypes || [];

  // Handle file selection
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    
    if (hasUploadedDocument) {
      toast({
        title: "Error",
        description: "Only one document allowed per document type. Please remove the existing document first.",
        variant: "destructive",
      });
      return;
    }
    
    if (!selectedType) {
      toast({
        title: "Error",
        description: "Please select a document type first",
        variant: "destructive",
      });
      return;
    }
    
    if (!selectedSubtype) {
      toast({
        title: "Error",
        description: "Please select a document sub-type first",
        variant: "destructive",
      });
      return;
    }
    
    if (files && files.length > 0) {
      const selectedFile = files[0];
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
      if (!allowedTypes.includes(selectedFile.type)) {
        e.target.value = '';
        return;
      }
      onFileUpload(selectedFile, selectedType, selectedSubtype, doc.id);
    }
    
    e.target.value = '';
  }, [selectedType, selectedSubtype, doc.id, onFileUpload, hasUploadedDocument]);

  // Handle file drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    onDragLeave();
    
    if (hasUploadedDocument) {
      toast({
        title: "Error",
        description: "Only one document allowed per document type. Please remove the existing document first.",
        variant: "destructive",
      });
      return;
    }
    
    if (!selectedType) {
      toast({
        title: "Error",
        description: "Please select a document type first",
        variant: "destructive",
      });
      return;
    }
    
    if (!selectedSubtype) {
      toast({
        title: "Error",
        description: "Please select a document sub-type first",
        variant: "destructive",
      });
      return;
    }
    
    const files = e.dataTransfer.files;
    
    if (files && files.length > 0) {
      const droppedFile = files[0];
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
      if (!allowedTypes.includes(droppedFile.type)) {
        return;
      }
      onFileUpload(droppedFile, selectedType, selectedSubtype, doc.id);
    }
  }, [selectedType, selectedSubtype, doc.id, onDragLeave, onFileUpload, hasUploadedDocument]);
  
  // Handle file download/viewing
  const openFileUrl = useCallback((url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  }, []);

  return (
    <Card className="relative">
      <CardContent className="p-4">
        {/* Card Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">{doc.name}</h3>
            {doc.required ? (
              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                Required
              </span>
            ) : (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                Optional
              </span>
            )}
          </div>
          {canRemoveCard && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemoveCard(doc.id)}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {/* Document Type - Show as text */}
          {hideDocumentTypeDropdown && (
            <div>
              <label className="text-sm font-medium mb-2 block">
                Document Type
              </label>
              <div className="flex items-center h-10 px-3 py-2 border rounded-md bg-gray-50">
                <FileText className="h-4 w-4 mr-2 text-gray-500" />
                <span className="text-sm font-medium">{doc.name}</span>
                {doc.code && <span className="text-xs ml-2 text-gray-500">({doc.code})</span>}
              </div>
            </div>
          )}

          {/* Document Subtype Dropdown - FIX: Don't disable when document is uploaded */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Document Sub Type
            </label>
            <Select
              value={selectedSubtype}
              onValueChange={(value) => {
                console.log(`Subtype changed for card ${doc.id}: ${value}`);
                onSubtypeChange(doc.id, value);
              }}
              disabled={!documentTypeCode || isDocumentSubtypeLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={
                  isDocumentSubtypeLoading
                    ? "Loading..."
                    : selectedSubtype || "Select Sub Type"
                } />
              </SelectTrigger>
              <SelectContent>
                {subtypes?.map((subtype: any) => (
                  <SelectItem key={subtype.id || subtype.code} value={subtype.name}>
                    {subtype.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isDocumentSubtypeLoading && (
              <p className="text-xs text-muted-foreground mt-1">
                Loading subtypes...
              </p>
            )}
            {!isDocumentSubtypeLoading && subtypes.length === 0 && documentTypeCode && !subtypeApiError && (
              <p className="text-xs text-amber-600 mt-1">
                No subtypes found for this document type
              </p>
            )}
            {subtypeApiError && (
              <p className="text-xs text-red-500 mt-1">
                {subtypeApiError}
              </p>
            )}
            {/* Show selected subtype if document is uploaded */}
            {hasUploadedDocument && selectedSubtype && (
              <p className="text-xs text-green-600 mt-1 flex items-center">
                <Check className="h-3 w-3 mr-1" />
                Currently selected: {selectedSubtype}
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Reference Number (optional)
            </label>
            <Input
              value={referenceNumber}
              onChange={(e) => onReferenceNumberChange(doc.id, e.target.value)}
              placeholder="Enter reference number"
            />
          </div>

          {/* File Upload Area or Uploaded Document */}
          {!hasUploadedDocument ? (
            <div className="py-2">
              <input
                type="file"
                onChange={handleFileChange}
                disabled={isUploadDisabled(doc.id)}
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                id={`file-upload-${doc.id}`}
              />
              <div className="flex items-center gap-3">
                <label 
                  htmlFor={`file-upload-${doc.id}`} 
                  className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-gray-200 bg-white shadow-sm hover:bg-gray-50 hover:text-gray-900 h-9 px-4 cursor-pointer ${isUploadDisabled(doc.id) ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <Upload className="h-4 w-4 mr-2 text-gray-500" />
                  Choose File
                </label>
                <p className="text-xs text-gray-500">
                  {isUploadDisabled(doc.id) ? "Select sub type to upload" : "Max 5MB (PDF, JPG, PNG)"}
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-sm mb-2 flex items-center">
                  <Check className="h-4 w-4 mr-1 text-green-500" />
                  Uploaded Document
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    console.log(`Removing document ${uploadedDocument.id} from card ${doc.id}`);
                    onRemoveDocument(uploadedDocument.id);
                  }}
                  className="h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              
              <div 
                className={`flex items-center mt-2 p-2 rounded border border-gray-200 bg-white ${uploadedDocument.url ? "cursor-pointer hover:bg-blue-50" : ""}`}
                onClick={() => uploadedDocument.url && openFileUrl(uploadedDocument.url)}
              >
                <FileText className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-sm flex-grow">{uploadedDocument.name}</span>
                {uploadedDocument.url && (
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
              </div>
              
              <div className="mt-2 flex items-center">
                <div className="text-xs text-gray-500">
                  Type: <span className="font-medium">{uploadedDocument.category}</span>
                  <span className="mx-1">•</span> 
                  Subtype: <span className="font-medium">{uploadedDocument.subCategory}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}